(function (root, factory) {
  var api = factory(root);

  root.PjDesignLabRenderer = api;

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }
})(typeof window !== 'undefined' ? window : globalThis, function (root) {
  'use strict';

  var ZOOM_DEFAULT = 1;
  var ZOOM_MIN = 0.4;
  var ZOOM_MAX = 4;
  var ZOOM_STEP = 0.25;

  function clampPercent(value) {
    var percent = Number(value);
    if (!Number.isFinite(percent)) return 0;
    return Math.min(100, Math.max(0, percent));
  }

  function clampZoom(value) {
    var scale = Number(value);
    if (!Number.isFinite(scale)) return ZOOM_DEFAULT;
    return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, scale));
  }

  // `=` is the unshifted plus key on a US keyboard; numpad reports `+` / `-`
  // or the older `Add` / `Subtract` keys depending on the browser.
  function zoomDeltaFromKey(event) {
    if (!event || event.ctrlKey || event.metaKey || event.altKey) return 0;

    var key = event.key;
    if (key === '+' || key === '=' || key === 'Add') return ZOOM_STEP;
    if (key === '-' || key === '_' || key === 'Subtract') return -ZOOM_STEP;

    var code = event.code;
    if (code === 'NumpadAdd') return ZOOM_STEP;
    if (code === 'NumpadSubtract') return -ZOOM_STEP;
    return 0;
  }

  function isEditableTarget(target) {
    if (!target) return false;
    var tag = String(target.tagName || '').toUpperCase();
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
    return Boolean(target.isContentEditable);
  }

  function normalizeMediaId(value) {
    var raw = String(value == null ? '' : value).trim();
    if (!raw) return '';
    var parts = raw.split('/');
    return parts[parts.length - 1] || raw;
  }

  function imageSource(image) {
    if (!image) return '';
    return (
      image.getAttribute('data-full-src') ||
      image.currentSrc ||
      image.getAttribute('src') ||
      image.src ||
      ''
    );
  }

  function resolveProductImage(doc, mediaId) {
    var targetId = normalizeMediaId(mediaId);
    if (!targetId || !doc || typeof doc.querySelectorAll !== 'function') return '';

    var media = doc.querySelectorAll('[data-media-id]');
    for (var index = 0; index < media.length; index += 1) {
      if (normalizeMediaId(media[index].getAttribute('data-media-id')) !== targetId) continue;
      var image =
        media[index].tagName === 'IMG'
          ? media[index]
          : media[index].querySelector && media[index].querySelector('img');
      var source = imageSource(image);
      if (source) return source;
    }

    return '';
  }

  // The base image comes from the template's media key only. Alt text and
  // data-title cannot tell a flat lay apart from a model shot, and a pet must
  // never land on model imagery, so an unconfigured color reports itself here
  // instead of guessing at a gallery slide.
  function listGalleryMedia(doc) {
    if (!doc || typeof doc.querySelectorAll !== 'function') return [];

    var nodes = doc.querySelectorAll('[data-media-id]');
    var seen = {};
    var media = [];

    for (var index = 0; index < nodes.length; index += 1) {
      var node = nodes[index];
      var mediaId = normalizeMediaId(node.getAttribute('data-media-id'));
      if (!mediaId || seen[mediaId]) continue;

      var image = node.tagName === 'IMG' ? node : node.querySelector && node.querySelector('img');
      seen[mediaId] = true;
      media.push({
        mediaId: mediaId,
        title:
          node.getAttribute('data-title') ||
          (image && typeof image.getAttribute === 'function' ? image.getAttribute('alt') : '') ||
          '',
      });
    }

    return media;
  }

  function selectedBackground(doc) {
    if (!doc || typeof doc.querySelector !== 'function') return '';
    var selected =
      doc.querySelector('input[name*="-Color-"]:checked') ||
      doc.querySelector('input[name*="-Background-"]:checked') ||
      doc.querySelector('input[name*="-Colour-"]:checked') ||
      doc.querySelector('input[name*="Color"]:checked');
    if (selected && selected.value) return String(selected.value).trim();

    var background = doc.querySelector(
      '.backgroundHiddenInput:not(.backgroundHiddenInputBandana)'
    );
    return background && background.value ? String(background.value).trim() : '';
  }

  function productColors(doc) {
    if (!doc || typeof doc.getElementById !== 'function') return [];

    var node = doc.getElementById('pjDesignLabPlacementRequiredColors');
    if (!node) return [];

    try {
      var parsed = JSON.parse(node.textContent || node.innerHTML || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  // Colors reach us three ways: chosen by the shopper, implied because the
  // product only sells one color, or not at all on a color-split product where
  // the lone template owns the page. Only a real multi-color product with
  // nothing chosen yet gets the ticket's Pink Dream default.
  function resolveColor(doc, contextColor) {
    var chosen = contextColor == null ? '' : String(contextColor).trim();
    if (chosen) return chosen;

    var colors = productColors(doc).filter(function (color) {
      return color != null && String(color).trim();
    });
    if (colors.length === 1) return String(colors[0]).trim();
    if (colors.length > 1) return 'Pink Dream';

    return '';
  }

  function buildComposition(placement, context) {
    var petImages = context && Array.isArray(context.petImages) ? context.petImages : [];
    if (!placement || !placement.ok) {
      return { ok: false, reason: (placement && placement.reason) || 'missing-template' };
    }
    if (!placement.imageUrl && !placement.mediaId) {
      return { ok: false, reason: 'missing-flat-lay' };
    }
    if (petImages.length < placement.petCount || petImages.slice(0, placement.petCount).some(function (url) {
      return !url;
    })) {
      return { ok: false, reason: 'missing-pet-image' };
    }

    var settings = context && Array.isArray(context.petSettings) ? context.petSettings : [];
    return {
      ok: true,
      color: placement.color,
      colorKey: placement.colorKey,
      mediaId: placement.mediaId,
      imageUrl: placement.imageUrl,
      pets: placement.slots.map(function (slot, index) {
        var petSetting = settings[index] || {};
        return {
          imageUrl: petSetting.processedImage || petImages[index],
          slot: slot,
          active: index === Math.max(0, Number(context && context.activePet) || 0),
          processed: Boolean(petSetting.processedImage),
          // A failed pet shows its untouched upload rather than the previous
          // mode's output, so no stale cutout is passed off as the new mode.
          processingFailed: Boolean(petSetting.processingError),
          extractionMode: petSetting.extractionMode || 'background',
          style: petSetting.style || '',
          accessory: petSetting.accessory || '',
          accessoryOverlay: petSetting.accessoryOverlay || null,
          scale: Number(petSetting.scale) > 0 ? Number(petSetting.scale) : 1,
          offsetX: Number(petSetting.offsetX) || 0,
          offsetY: Number(petSetting.offsetY) || 0,
          eraseMaskUrl: petSetting.eraseMaskUrl || '',
        };
      }),
    };
  }

  function createPreviewRenderer(doc, options) {
    var config = options || {};
    var context = {};
    var zoomScale = ZOOM_DEFAULT;
    var initialized = false;

    function region(name) {
      var modal = doc.getElementById('pvModal');
      return modal && modal.querySelector('[data-pv-region="' + name + '"]');
    }

    function showError(reason) {
      var loaded = region('loaded');
      var error = region('error');
      // Every suppression reason is logged, not just the template ones: a blank
      // canvas with no console trail is undiagnosable from a QA report.
      if (root.console && typeof root.console.warn === 'function') {
        root.console.warn('[PJ Design Lab] Preview unavailable.', {
          reason: reason,
          petCount: context.petCount,
          petImages: context.petImages,
          petSettings: context.petSettings,
        });
      }
      if (loaded) loaded.hidden = true;
      if (!error) return false;

      error.hidden = false;
      error.innerHTML = '';

      var panel = doc.createElement('div');
      panel.className = 'pv-render-error';

      var title = doc.createElement('strong');
      title.textContent = 'Preview unavailable';

      var message = doc.createElement('p');
      message.textContent =
        reason === 'missing-template' || reason === 'missing-slots'
          ? 'This pajama color is not configured for preview yet.'
          : 'We could not load the product preview. Your uploaded photos are still safe.';

      panel.appendChild(title);
      panel.appendChild(message);
      error.appendChild(panel);
      return false;
    }

    function compositeElement() {
      var loaded = region('loaded');
      if (!loaded) return null;
      if (typeof loaded.querySelector === 'function') {
        return loaded.querySelector('.pv-composite');
      }
      return Array.prototype.find.call(loaded.children || [], function (child) {
        return child.className === 'pv-composite';
      });
    }

    function applyZoom() {
      var frame = compositeElement();
      if (frame) frame.style.transform = 'scale(' + zoomScale + ')';
      return zoomScale;
    }

    function setZoom(nextScale) {
      zoomScale = clampZoom(nextScale);
      return applyZoom();
    }

    function zoomIn() {
      return setZoom(zoomScale + ZOOM_STEP);
    }

    function zoomOut() {
      return setZoom(zoomScale - ZOOM_STEP);
    }

    function resetZoom() {
      return setZoom(ZOOM_DEFAULT);
    }

    function createPet(pet, index) {
      var image = doc.createElement('img');
      image.className = 'pv-composite-pet';
      if (pet.extractionMode === 'background') image.classList.add('mode-background');
      if (pet.processingFailed) image.classList.add('is-unprocessed');
      if (pet.active) image.classList.add('sel-ring');
      image.src = pet.imageUrl;
      image.alt = 'Uploaded pet ' + (index + 1);
      image.dataset.petIndex = String(index);
      // Reposition is clamped to the slot's own size so a shopper can't drag
      // a pet fully off the flat-lay it's meant to sit on.
      image.style.left = clampPercent(pet.slot.x + (Number(pet.offsetX) || 0)) + '%';
      image.style.top = clampPercent(pet.slot.y + (Number(pet.offsetY) || 0)) + '%';
      image.style.width = pet.slot.width * pet.scale + '%';
      image.style.transform =
        'translate(-50%, -50%) rotate(' + (Number(pet.slot.rotation) || 0) + 'deg)';

      // Erase/restore edits are a "keep" stencil (opaque = keep, transparent
      // = erased) applied over this pet's own photo via CSS mask - never
      // swapped in as the image itself - so each pet keeps its own picture
      // even when "apply this look to all pets" shares the same stencil.
      //
      // mask-mode is forced to "alpha" rather than left at its "match-source"
      // default: WebKit (Safari, and therefore every browser on iOS) has long
      // defaulted a raster mask-image to *luminance* mode, under which an
      // opaque-but-dark stencil pixel reads as "hide" instead of "keep". The
      // stencil itself is painted white for "keep" (see pj-design-lab-review.js)
      // as a second layer of defense, but the mode is pinned explicitly too so
      // this never again depends on a browser's unstated default.
      if (pet.eraseMaskUrl) {
        var maskValue = 'url("' + pet.eraseMaskUrl + '")';
        image.style.maskImage = maskValue;
        image.style.webkitMaskImage = maskValue;
        image.style.maskMode = 'alpha';
        image.style.webkitMaskMode = 'alpha';
        image.style.maskSize = '100% 100%';
        image.style.webkitMaskSize = '100% 100%';
        image.style.maskRepeat = 'no-repeat';
        image.style.webkitMaskRepeat = 'no-repeat';
      }
      return image;
    }

    // The accessory is a sibling of the pet's image rather than a wrapper
    // around it, so the review editor keeps finding .pv-composite-pet exactly
    // where it did. .pv-composite-pet is aspect-ratio: 1, so a pet's box is
    // square and the catalog's placement percentages - both measured against
    // that box, as the wireframe measures them - transfer directly.
    //
    // The vertical offset is expressed as a share of the accessory's own
    // height: a `top` percentage resolves against the composite's height,
    // while the pet's box is sized off the composite's width, so the two are
    // not interchangeable. Ordering the transform as centre -> rotate -> lift
    // also keeps the accessory square to a rotated slot.
    function createAccessory(pet, index) {
      var overlay = pet.accessoryOverlay;
      // Both the artwork and its geometry come from the catalog, and the
      // controls layer only hands over an overlay once it has both - there is
      // no geometry to invent here, so an incomplete one simply draws nothing.
      var placement = overlay && overlay.placement;
      if (!overlay || !overlay.asset || !placement) return null;

      var width = Number(placement.width);
      var bottom = Number(placement.bottom);
      if (!Number.isFinite(width) || width <= 0 || !Number.isFinite(bottom)) return null;

      // Distance from the pet's centre up to the accessory's bottom edge,
      // restated as a percentage of the accessory's own height.
      var lift = 50 + ((bottom - 50) / width) * 100;

      var image = doc.createElement('img');
      image.className = 'pv-acc-overlay';
      image.src = overlay.asset;
      image.alt = '';
      image.dataset.petIndex = String(index);
      image.style.left = clampPercent(pet.slot.x + (Number(pet.offsetX) || 0)) + '%';
      image.style.top = clampPercent(pet.slot.y + (Number(pet.offsetY) || 0)) + '%';
      image.style.width = pet.slot.width * pet.scale * (width / 100) + '%';
      image.style.transform =
        'translate(-50%, -50%) rotate(' +
        (Number(pet.slot.rotation) || 0) +
        'deg) translateY(-' +
        lift +
        '%)';
      return image;
    }

    function render(nextContext) {
      if (nextContext) context = Object.assign({}, context, nextContext);

      var placementApi = config.placement || root.PjDesignLabPlacement;
      var loadedPlacement = placementApi && placementApi.current && placementApi.current();
      var color = (config.resolveColor || resolveColor)(doc, context.color);
      var placement =
        loadedPlacement &&
        placementApi.getSlots(loadedPlacement.catalog, {
          color: color,
          mediaId: context.mediaId,
          petCount: context.petCount,
        });
      if (
        !placement ||
        (!placement.ok &&
          ['missing-catalog', 'missing-template', 'missing-slots'].indexOf(placement.reason) >= 0)
      ) {
        if (root.console && typeof root.console.warn === 'function') {
          root.console.warn(
            '[PJ Design Lab] Preview suppressed: no usable placement template.',
            {
              reason: (placement && placement.reason) || 'missing-template',
              requestedColor: color || '(no color axis on this product)',
              requestedColorKey:
                placementApi && placementApi.normalizeColorKey
                  ? placementApi.normalizeColorKey(color)
                  : color,
              requestedPetCount: context.petCount,
              placement: placement,
              galleryMedia: (config.listGalleryMedia || listGalleryMedia)(doc),
              diagnostics:
                placementApi && placementApi.diagnose ? placementApi.diagnose(doc) : null,
              hint: 'Compare requestedColorKey against templates[].colorKey, and requestedPetCount against templates[].petCountsConfigured.',
            }
          );
        }
      }
      var composition = buildComposition(placement, context);
      if (!composition.ok) return showError(composition.reason);

      var flatLay =
        composition.imageUrl ||
        (config.resolveProductImage || resolveProductImage)(doc, composition.mediaId);
      if (!flatLay) return showError('missing-flat-lay');

      var loaded = region('loaded');
      var error = region('error');
      if (!loaded) return false;

      loaded.innerHTML = '';
      loaded.hidden = false;
      if (error) error.hidden = true;

      var frame = doc.createElement('div');
      frame.className = 'pv-composite';
      frame.dataset.color = composition.colorKey;
      frame.dataset.mediaId = composition.mediaId;
      frame.style.transform = 'scale(' + zoomScale + ')';

      var base = doc.createElement('img');
      base.className = 'pv-composite-base';
      base.src = flatLay;
      base.alt = composition.color + ' pajamas preview';
      frame.appendChild(base);

      var overlay = doc.createElement('div');
      overlay.className = 'pv-composite-overlay';
      composition.pets.forEach(function (pet, index) {
        overlay.appendChild(createPet(pet, index));
        // Appended after the pet so it paints over it.
        var accessory = createAccessory(pet, index);
        if (accessory) overlay.appendChild(accessory);
      });
      frame.appendChild(overlay);
      loaded.appendChild(frame);

      // The composite is torn down and rebuilt from scratch on every render,
      // so anything overlaid on a pet image (the review editor's drag/erase
      // surface) needs to know to re-attach itself to the new element.
      if (typeof doc.dispatchEvent === 'function' && typeof root.CustomEvent === 'function') {
        doc.dispatchEvent(
          new root.CustomEvent('pj-design-lab:composite-rendered', {
            detail: { activePet: Math.max(0, Number(context.activePet) || 0) },
          })
        );
      }
      return true;
    }

    function setContext(nextContext) {
      context = Object.assign({}, context, nextContext || {});
      return context;
    }

    function onLoadingComplete() {
      render();
    }

    function onLoadingFailed() {
      render();
    }

    function onVariantChange() {
      if (!context || !context.petCount) return;
      context.color = selectedBackground(doc);
      render();
    }

    function onZoomClick(event) {
      var target = event && event.target;
      if (!target) return;
      if (target.id === 'pvZoomIn') zoomIn();
      if (target.id === 'pvZoomOut') zoomOut();
    }

    function previewIsOpen() {
      var modal = doc.getElementById('pvModal');
      return Boolean(modal && modal.classList && modal.classList.contains('open'));
    }

    function onZoomKeydown(event) {
      if (!previewIsOpen() || isEditableTarget(event && event.target)) return;
      var delta = zoomDeltaFromKey(event);
      if (!delta) return;
      if (typeof event.preventDefault === 'function') event.preventDefault();
      setZoom(zoomScale + delta);
    }

    function init() {
      if (initialized || !doc || typeof doc.addEventListener !== 'function') return;
      initialized = true;
      doc.addEventListener('pj-design-lab:preview-loading-complete', onLoadingComplete);
      doc.addEventListener('pj-design-lab:preview-loading-failed', onLoadingFailed);
      doc.addEventListener('variant:change', onVariantChange);
      doc.addEventListener('change', onVariantChange);
      doc.addEventListener('click', onZoomClick);
      doc.addEventListener('keydown', onZoomKeydown);
    }

    init();

    return {
      setContext: setContext,
      render: render,
      setZoom: setZoom,
      zoomIn: zoomIn,
      zoomOut: zoomOut,
      resetZoom: resetZoom,
      init: init,
    };
  }

  var controller = root.document ? createPreviewRenderer(root.document) : null;

  return {
    normalizeMediaId: normalizeMediaId,
    resolveProductImage: resolveProductImage,
    listGalleryMedia: listGalleryMedia,
    selectedBackground: selectedBackground,
    productColors: productColors,
    resolveColor: resolveColor,
    buildComposition: buildComposition,
    clampPercent: clampPercent,
    clampZoom: clampZoom,
    zoomDeltaFromKey: zoomDeltaFromKey,
    createPreviewRenderer: createPreviewRenderer,
    setContext: controller && controller.setContext,
    render: controller && controller.render,
    setZoom: controller && controller.setZoom,
    zoomIn: controller && controller.zoomIn,
    zoomOut: controller && controller.zoomOut,
    resetZoom: controller && controller.resetZoom,
    init: controller && controller.init,
  };
});
